import { useState } from 'react'
import { useSelector } from 'react-redux'
import { useNavigate } from 'react-router-dom'
import { State } from 'src/state'
import { Screen } from 'src/screens'
import { Modal, Header, ModalContent, Section, OptionTile, SizeButtons, Footer, TotalDisplay } from './styles'
import CloseButton from 'src/global/CloseButton'
import { Backdrop } from 'src/global'
import { MenuSize, Priority, NutritionalChoice, MENU_SIZES, calculateOnboardingPackagePrice, isPaying } from '@meniudigital/shared'
import { userEndpoints } from 'src/api'
import { markOnboardingPackageRequested } from 'src/helpers/onboardingPackage'
import { FontAwesomeIcon } from '@fortawesome/react-fontawesome'
import { faTriangleExclamation } from '@fortawesome/free-solid-svg-icons'
import useWindowModal from 'src/helpers/useWindowModal'

export default function OnboardingPackageModal() {
  const { user } = useSelector((state: State) => state.users)
  const navigate = useNavigate()
  const userEmail = user?.email || user?.username || ''
  const hasActiveSubscription = isPaying(user)

  const { isOpen, close } = useWindowModal('onboarding-package-modal:change', (openModal, closeModal) => {
    window.openOnboardingPackageModal = openModal
    window.hideOnboardingPackageModal = closeModal
  })

  const [size, setSize] = useState<MenuSize>('0-100')
  const [weHandlePhotos, setWeHandlePhotos] = useState(false)
  const [nutritional, setNutritional] = useState<NutritionalChoice>('later')
  const [priority, setPriority] = useState<Priority>('standard')
  const [isLoading, setIsLoading] = useState(false)

  const goToSubscription = () => {
    close()
    navigate(Screen.MySubscription)
  }

  const totalPrice = calculateOnboardingPackagePrice({ size, weHandlePhotos, nutritional, priority })

  const submit = async () => {
    if (!hasActiveSubscription) {
      return
    }

    await window.showConfirm({
      title: 'Confirmă cererea',
      textComponent: (
        <>
          <p>
            Vei primi un e-mail la <strong>{userEmail}</strong> cu lista informațiilor de care avem nevoie; răspunde cu acestea
            când le ai pregătite.
          </p>
          <br />
          <p>
            Ești sigur că dorești să trimiți cererea pentru Digitalizare Meniu în valoare de <strong>{totalPrice} RON</strong>?
          </p>
        </>
      ),
      isConfirm: true,
      confirmButtonText: 'Trimite cererea',
      cancelButtonText: 'Înapoi',
    })

    setIsLoading(true)

    try {
      await userEndpoints.requestOnboardingPackage({ size, weHandlePhotos, nutritional, priority })
      markOnboardingPackageRequested()
      window.dispatchEvent(new Event('onboarding-package-submitted'))
      setIsLoading(false)
      close()
      window
        .showConfirm({
          title: 'Cererea a fost trimisă',
          textComponent: (
            <>
              <p>
                Ți-am trimis un email la <strong>{userEmail}</strong> cu rezumatul cererii și lista informațiilor de care avem
                nevoie.
              </p>
              <br />
              <p>
                Răspunde la acel email atașând materialele cerute. După ce le primim, te contactăm cu detalii despre plată și
                începem configurarea.
              </p>
            </>
          ),
        })
        .catch(() => {})
    } catch {
      setIsLoading(false)
      await window.showModal({ title: 'Eroare', text: 'A apărut o eroare. Încearcă din nou.' })
    }
  }

  return (
    <>
      <Backdrop onClick={close} className={isOpen ? 'open' : 'closed'} />
      <Modal className={isOpen ? 'open' : 'closed'}>
        <Header>
          <div className="title-wrapper">
            <div className="title">
              Digitalizare Meniu
              <span className="badge">NOU</span>
            </div>
          </div>
          <CloseButton onClick={close} title="Închide" color="#ffffff" style={{ marginTop: '-16px', marginRight: '-9px' }} />
        </Header>
        <ModalContent>
          <Section className="intro">
            <p>Dorești să-ți importăm noi meniul? Completează mai jos detaliile pentru a obține o estimare de preț.</p>
          </Section>

          <Section>
            <h3>Câte produse ai în meniu?</h3>
            <p className="hint">Variantele se numără ca produse separate (ex. 3 mărimi pizza = 3 produse).</p>
            <SizeButtons>
              {MENU_SIZES.map(option => (
                <button key={option} className={size === option ? 'active' : ''} onClick={() => setSize(option)}>
                  {option}
                </button>
              ))}
            </SizeButtons>
          </Section>

          <Section>
            <h3>Ne trimiți și imaginile aferente produselor?</h3>
            <OptionTile $active={weHandlePhotos} onClick={() => setWeHandlePhotos(true)}>
              <div className="check" />
              <div className="content">
                <div className="option-title">Vi le trimit, le încărcați voi</div>
                <div className="description">Ne trimiți pozele într-un folder, le asociem fiecărui produs.</div>
              </div>
            </OptionTile>
            <OptionTile $active={!weHandlePhotos} onClick={() => setWeHandlePhotos(false)}>
              <div className="check" />
              <div className="content">
                <div className="option-title">Le încarc eu mai târziu</div>
                <div className="description">Produsele rămân fără poze. Le adaugi tu din interfață când dorești.</div>
              </div>
            </OptionTile>
          </Section>

          <Section>
            <h3>Declarația nutrițională la produse?</h3>
            <OptionTile $active={nutritional === 'provided'} onClick={() => setNutritional('provided')}>
              <div className="check" />
              <div className="content">
                <div className="option-title">Există deja, v-o trimit</div>
                <div className="description">Detaliile produselor includ și declarația nutrițională</div>
              </div>
            </OptionTile>
            <OptionTile $active={nutritional === 'weGenerate'} onClick={() => setNutritional('weGenerate')}>
              <div className="check" />
              <div className="content">
                <div className="option-title">O generați voi</div>
                <div className="description">Calorii și alergeni pentru fiecare produs, pe baza ingredientelor.</div>
              </div>
            </OptionTile>
            <OptionTile $active={nutritional === 'later'} onClick={() => setNutritional('later')}>
              <div className="check" />
              <div className="content">
                <div className="option-title">O adaug eu mai târziu</div>
                <div className="description">Produsele rămân fără declarație nutrițională.</div>
              </div>
            </OptionTile>
          </Section>

          <Section>
            <h3>În cât timp îți dorești meniul gata?</h3>
            <OptionTile $active={priority === 'standard'} onClick={() => setPriority('standard')}>
              <div className="check" />
              <div className="content">
                <div className="option-title">Standard</div>
                <div className="description">Termen de livrare: 4-5 zile lucrătoare.</div>
              </div>
            </OptionTile>
            <OptionTile $active={priority === 'urgent'} onClick={() => setPriority('urgent')}>
              <div className="check" />
              <div className="content">
                <div className="option-title">Urgent</div>
                <div className="description">Termen de livrare: ~2 zile lucrătoare.</div>
              </div>
            </OptionTile>
          </Section>

          <Section className="bonus">
            <div className="bonus-header">
              <h3>Profil complet</h3>
              <span className="free-tag">GRATUIT</span>
            </div>
            <p>
              Pe lângă importul produselor, configurăm și restul contului tău fără cost suplimentar, dacă ne trimiți următoarele
              detalii:
            </p>
            <ul className="benefits">
              <li>Logo și poza de cover</li>
              <li>Orar de funcționare</li>
              <li>Locație pe hartă</li>
              <li>Mesaj custom pe meniu</li>
              <li>Info WiFi și social media</li>
              <li>Nume + oraș formatate corect</li>
            </ul>
            <p className="config-note">Toate aceste detalii vor fi configurabile și de tine ulterior, din interfața de administrare.</p>
          </Section>

          <Section className="next-steps">
            <p>
              După trimiterea cererii, vei primi un mail la <strong>{userEmail}</strong> cu opțiunile selectate și o listă cu
              informațiile necesare.
            </p>
            <p>
              Plata se va face după recepționarea materialelor atașate și confirmarea că avem toate informațiile pentru a începe
              configurarea.
            </p>
          </Section>

          {!hasActiveSubscription && (
            <div className="subscription-warning" onClick={goToSubscription}>
              <FontAwesomeIcon icon={faTriangleExclamation} />
              <span>Ai nevoie de un abonament activ pentru a trimite cererea.</span>
            </div>
          )}
        </ModalContent>
        <Footer>
          <TotalDisplay>
            <span className="label">Total pentru opțiunile selectate</span>
            <span className="amount">{totalPrice} RON</span>
          </TotalDisplay>
          <button className="cta" onClick={submit} disabled={!hasActiveSubscription || isLoading}>
            {isLoading ? 'Se trimite...' : 'Trimite cerere'}
          </button>
        </Footer>
      </Modal>
    </>
  )
}
